/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import * as Yup from 'yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container, Box } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import SupportTextarea from '@app/features/Support/SupportTextarea';
import {
  QaFileAttachments,
  QaFileAttachmentsState,
  QaFileAttachmentsHandle,
} from '@features/Support/QaFileUploadList';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Local API & Types
import {
  GetOneQuestionQueryVariables,
  GetThreadQueryVariables,
  ResourceIndexView,
  SortDirection,
  useReplyInThreadMutation,
} from '@app-types/blackroom-api';
import {
  DialogCreateFollowUpFormSchema,
  DialogCreateFollowUpProps,
} from './types';

// Style
import * as classes from './styles/index.css';
import GET_ONE_QUESTION from '@app/features/Support/api/getOneQuestion';
import GET_THREAD from '@app/features/Support/api/getThread';
import ResourceSelector from '@features/AIAssistant/PageAIAssistant/SectionRename/ResourceSelector';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogCreateFollowUp({ questionId }: DialogCreateFollowUpProps) {
  /* ********************************** HOOKS ********************************* */

  // Ref
  const focusRef = useRef<HTMLInputElement>(null);
  const fileStateRef = useRef<QaFileAttachmentsState>({
    externalFiles: [],
    isUploading: false,
    hasFiles: false,
  });
  const fileAttachmentsRef = useRef<QaFileAttachmentsHandle>(null);

  // State
  const [openResourceTable, setOpenResourceTable] = useState<boolean>(false);
  const [selectedResources, setSelectedResources] = useState<
    ResourceIndexView[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { dialogIdentifier, closeDialog } = dialogStore ?? {};

  // API
  const [replyInThread] = useReplyInThreadMutation();

  // Locale
  const { t } = useTranslation(['Common', 'Support']);

  // Form
  const formSchema = Yup.object().shape({
    threadSourceId: Yup.string().required(),
    description: Yup.string()
      .trim()
      .required(t('Common:validation.required') as string),
    resourceIds: Yup.array().of(Yup.string()),
  });

  const form = useForm<DialogCreateFollowUpFormSchema>({
    initialValues: {
      threadSourceId: questionId,
      description: '',
    },
    validate: yupResolver(formSchema),
    onValuesChange: () => {
      form.validate();
    },
  });

  // Side-effects
  useEffect(
    function onMount() {
      if (dialogIdentifier === 'dialog-support-create-follow-up') {
        focusRef.current?.focus();
      }
    },
    [dialogIdentifier],
  );

  /* ******************************** CALLBACKS ******************************* */

  const handleFileStateChange = useCallback((state: QaFileAttachmentsState) => {
    fileStateRef.current = state;
    setIsUploading(state.isUploading);
  }, []);

  async function onSubmit({ ...formArgs }: DialogCreateFollowUpFormSchema) {
    closeDialog?.('dialog-support-create-follow-up');

    const { externalFiles } = fileStateRef.current;
    const newResources = externalFiles.length > 0 ? externalFiles : undefined;
    fileAttachmentsRef.current?.reset();

    try {
      await replyInThread({
        variables: {
          replyInThreadInput: {
            ...formArgs,
            resourceIds: selectedResources.map((r) => r.id),
            newResources,
          },
        },
        refetchQueries: [
          {
            query: GET_ONE_QUESTION,
            variables: {
              questionId,
            } as GetOneQuestionQueryVariables,
          },
          {
            query: GET_THREAD,
            variables: {
              filter: { threadSourceId: questionId },
              sorter: { createdAt: SortDirection.Asc },
            } as GetThreadQueryVariables,
          },
        ],
      });
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Support:Common.errors.readonlyAccess'),
        color: 'blackroomBlack',
      });
      console.error(error);
    }
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Container w="720px" p={0} fluid>
      <Stack align="stretch" justify="space-between" gap="lg" h="100%">
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack gap="0">
            <SupportTextarea {...form.getInputProps('description')} />

            <Stack align="flex-start" gap={0}>
              <QaFileAttachments
                ref={fileAttachmentsRef}
                onAttachResource={() =>
                  setOpenResourceTable(!openResourceTable)
                }
                resources={selectedResources}
                onRemoveResource={(resourceId) =>
                  setSelectedResources(
                    selectedResources.filter((r) => r.id !== resourceId),
                  )
                }
                onStateChange={handleFileStateChange}
                buttonSectionClassName={classes.selectResourceButtonSection}
              />
              {openResourceTable && (
                <Box h={400} w="100%" mt="sm">
                  <ResourceSelector
                    selectedResources={selectedResources}
                    setSelectedResources={setSelectedResources}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </form>
        <ConfirmBar
          labelProps={{
            confirm: t('Common:cta.save'),
            cancel: t('Common:cta.cancel'),
          }}
          onConfirm={form.onSubmit(function cb(formValues) {
            onSubmit(formValues);
          })}
          onCancel={() => {
            dialogStore?.closeDialog?.('dialog-support-create-follow-up');
          }}
          disabled={!form.isValid() || isUploading}
        />
      </Stack>
    </Container>
  );
}

/* **************************** DIALOG INVOKATION *************************** */

export default DialogCreateFollowUp;
